import { useState, useCallback } from 'react';

import type { AttachedFile, AttachmentType } from '../types';

import { uploadFileToApi } from '@/lib/api';

const ALLOWED_CONTENT_TYPES: Record<string, AttachmentType> = {
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILE_COUNT = 5;

let nextFileId = 0;
const generateFileId = () => `file-${++nextFileId}`;

interface UseFileAttachmentOptions {
  uploadEndpoint?: string;
  headers?: Record<string, string>;
  getConversationId: () => string;
  onError?: (message: string) => void;
}

export function useFileAttachment({ uploadEndpoint, headers, getConversationId, onError }: UseFileAttachmentOptions) {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) URL.revokeObjectURL(file.preview);

      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const validFiles = files.filter((file) => {
        if (!ALLOWED_CONTENT_TYPES[file.type]) {
          onError?.('Image type is not supported. Please use JPEG, PNG, GIF, or WebP.');

          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          onError?.(`Image "${file.name}" exceeds the 10MB size limit.`);

          return false;
        }

        return true;
      });

      const remaining = MAX_FILE_COUNT - attachedFiles.length;

      if (remaining === 0) {
        onError?.('Maximum 5 images reached.');

        return;
      }

      const filesToAdd = validFiles.slice(0, remaining);

      if (filesToAdd.length === 0) {
        return;
      }

      if (filesToAdd.length < validFiles.length) {
        onError?.('Maximum 5 images. Some images were not attached.');
      }

      if (!uploadEndpoint) {
        console.error('[useFileAttachment] uploadEndpoint is not configured — file attachment is unavailable.');
        onError?.('Something went wrong. Please try again later.');

        return;
      }

      const newFiles: AttachedFile[] = filesToAdd.map((file) => ({
        id: generateFileId(),
        file,
        preview: URL.createObjectURL(file),
        status: 'uploading' as const,
        attachmentType: ALLOWED_CONTENT_TYPES[file.type],
      }));

      setAttachedFiles((prev) => [...prev, ...newFiles]);

      const conversationId = getConversationId();
      newFiles.forEach((newFile) => {
        void (async () => {
          try {
            const { publicUrl } = await uploadFileToApi({
              endpoint: uploadEndpoint,
              conversationId,
              file: newFile.file,
              headers,
            });

            setAttachedFiles((prev) => prev.map((f) => (f.id === newFile.id ? { ...f, status: 'uploaded', publicUrl } : f)));
          } catch (error) {
            console.error('Upload failed:', error);
            onError?.(`Failed to upload "${newFile.file.name}". Please try again.`);
            setAttachedFiles((prev) => {
              const file = prev.find((f) => f.id === newFile.id);
              if (file) URL.revokeObjectURL(file.preview);

              return prev.filter((f) => f.id !== newFile.id);
            });
          }
        })();
      });
    },
    [attachedFiles, uploadEndpoint, headers, getConversationId, onError],
  );

  const clearAttachedFiles = useCallback(() => {
    attachedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    setAttachedFiles([]);
  }, [attachedFiles]);

  const getUploadedAttachments = useCallback(() => {
    return attachedFiles
      .filter((f) => f.status === 'uploaded' && f.publicUrl)
      .map((f) => ({ type: f.attachmentType, url: f.publicUrl!, filename: f.file.name }));
  }, [attachedFiles]);

  const isUploading = attachedFiles.some((f) => f.status === 'uploading');

  return {
    attachedFiles,
    isUploading,
    addFiles,
    removeFile,
    clearAttachedFiles,
    getUploadedAttachments,
  };
}
